import { PACKAGE_STATUS_LABELS, type PackageStatus } from '@azko/shared';

/**
 * A API sempre entrega ISO em UTC. Toda conversao para o fuso do usuario
 * acontece aqui, num lugar so — e o que evita o classico "a encomenda das
 * 17:42 aparece como 20:42" quando o servidor roda em outra regiao.
 */
const TIME = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const DATE = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

export function formatTime(iso: string): string {
  return TIME.format(new Date(iso));
}

/** "Hoje, 17:42" / "Ontem, 20:44" / "07/08, 09:15". */
export function formatReceived(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 86_400_000);

  if (date >= startOfToday) return `Hoje, ${TIME.format(date)}`;
  if (date >= startOfYesterday) return `Ontem, ${TIME.format(date)}`;
  return `${DATE.format(date)}, ${TIME.format(date)}`;
}

export function statusLabel(status: PackageStatus): string {
  return PACKAGE_STATUS_LABELS[status];
}

export function statusTone(status: PackageStatus): 'accent' | 'neutral' | 'warn' {
  if (status === 'awaiting') return 'accent';
  // Cancelada nao e um fim normal como "Retirada": e um registro que nao
  // deveria existir. Sai do cinza para que quem varre a lista filtrada
  // encontre o que procura sem ler linha por linha.
  if (status === 'canceled') return 'warn';
  return 'neutral';
}

/** "8s" / "1m 20s" — o card de tempo medio de confirmacao. */
export function formatSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

/** Telefone E.164 -> "(31) 9 9999-1122". */
export function formatPhone(e164: string | null): string {
  if (!e164) return '—';
  const digits = e164.replace(/\D/g, '').replace(/^55/, '');
  if (digits.length < 10) return e164;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  return rest.length === 9
    ? `(${ddd}) ${rest[0]} ${rest.slice(1, 5)}-${rest.slice(5)}`
    : `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
}

// ---------------------------------------------------------------------------
// Mascaras de digitacao
// ---------------------------------------------------------------------------
//
// O banco guarda so digitos (`phone_e164`, `document`) e a mascara e assunto
// da UI — as duas funcoes abaixo sao os dois lados dessa fronteira. Elas vivem
// aqui, e nao dentro do componente, porque a mesma conversao acontece em tres
// telas (morador, porteiro, assinante) e uma copia divergente significaria um
// telefone gravado com DDI e outro sem.

/**
 * Telefone brasileiro enquanto se digita: "(31) 9 9999-1122".
 *
 * Corta em 11 digitos (DDD + 9 do celular) e monta o formato conforme o que ja
 * foi digitado, sem esperar o campo ficar completo — mascara que so aparece no
 * fim faz o usuario duvidar se o campo esta funcionando. O DDI **nao** entra na
 * mascara: brasileiro nao digita "+55", e mostrar isso no campo faria alguem
 * digitar o 55 de novo.
 */
export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '').slice(0, 11);

  if (digits.length <= 2) return digits.length > 0 ? `(${digits}` : '';
  const ddd = `(${digits.slice(0, 2)}) `;
  const rest = digits.slice(2);

  // Celular tem 9 digitos e o primeiro fica isolado — e assim que o numero e
  // lido em voz alta no Brasil, e a leitura e o que o sindico confere.
  if (rest.length <= 4) return ddd + rest;
  if (rest.length <= 8) return `${ddd}${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `${ddd}${rest[0]} ${rest.slice(1, 5)}-${rest.slice(5)}`;
}

/**
 * Do campo mascarado para o que a API espera: E.164 sem "+".
 *
 * Devolve string vazia quando o campo esta vazio — e o que apaga um telefone
 * errado. Numero incompleto sai como esta e o backend recusa com uma mensagem;
 * inventar um DDI sobre 4 digitos gravaria lixo com cara de telefone valido.
 */
export function phoneToE164(masked: string): string {
  const digits = masked.replace(/\D/g, '');
  if (digits === '') return '';
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
}

/** CPF ("123.456.789-01") ou CNPJ ("12.345.678/0001-90"), pelo tamanho. */
export function maskDocument(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

export function formatCurrency(cents: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(cents / 100);
}

/** Variacao percentual dos cards ("+18% vs. ontem"). */
export function formatDelta(current: number, previous: number): string {
  if (previous === 0) return current > 0 ? 'sem base de ontem' : 'sem movimento';
  const delta = Math.round(((current - previous) / previous) * 100);
  return `${delta >= 0 ? '+' : ''}${delta}% vs. ontem`;
}
